import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import type { BannedKeyword, BannedKeywordAction } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldBan, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';

const ACTION_LABEL: Record<BannedKeywordAction, string> = {
  block: '차단',
  mask: '탐지만',
};

export function AdminBannedKeywords() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'banned-keywords'],
    queryFn: () => api.adminBannedKeywordList(),
  });

  const [term, setTerm] = useState('');
  const [action, setAction] = useState<BannedKeywordAction>('block');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'banned-keywords'] });

  const createMut = useMutation({
    mutationFn: () =>
      api.adminBannedKeywordCreate({ term: term.trim(), action, note: note.trim() || null }),
    onSuccess: () => {
      setTerm('');
      setNote('');
      setAction('block');
      setFormError(null);
      invalidate();
    },
    onError: (err: unknown) =>
      setFormError(err instanceof ApiError ? err.message : '추가에 실패했습니다.'),
  });

  const updateMut = useMutation({
    mutationFn: (v: { id: string; patch: Parameters<typeof api.adminBannedKeywordUpdate>[1] }) =>
      api.adminBannedKeywordUpdate(v.id, v.patch),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.adminBannedKeywordDelete(id),
    onSuccess: invalidate,
  });

  const items = data?.keywords ?? [];

  const onAdd = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (term.trim().length < 1) {
      setFormError('금칙어를 입력하세요.');
      return;
    }
    createMut.mutate();
  };

  return (
    <PageShell
      header={
        <PageHeader
          title="금칙어 관리"
          count={items.length}
          description="욕설·부적절 표현 블록리스트입니다. 탐지는 항상 동작하며, 실제 차단은 대시보드의 '금칙어 차단 적용' 토글이 켜져 있고 action='차단' 인 항목에만 적용됩니다."
        />
      }
    >
      <form
        onSubmit={onAdd}
        className="mb-6 rounded-2xl border border-border bg-surface p-4 flex flex-wrap items-end gap-3"
      >
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="bk-term" className="block text-xs font-medium text-foreground-muted mb-1">
            금칙어 (단어 또는 구)
          </label>
          <Input
            id="bk-term"
            value={term}
            maxLength={200}
            placeholder="예: 욕설표현"
            onChange={(e) => setTerm(e.currentTarget.value)}
          />
        </div>
        <div>
          <label
            htmlFor="bk-action"
            className="block text-xs font-medium text-foreground-muted mb-1"
          >
            동작
          </label>
          <select
            id="bk-action"
            value={action}
            onChange={(e) => setAction(e.currentTarget.value as BannedKeywordAction)}
            className="h-9 rounded-md border border-border bg-surface px-3 text-sm focus:border-accent focus:outline-none"
          >
            <option value="block">차단</option>
            <option value="mask">탐지만</option>
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label htmlFor="bk-note" className="block text-xs font-medium text-foreground-muted mb-1">
            메모 (선택)
          </label>
          <Input
            id="bk-note"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        </div>
        <Button type="submit" disabled={createMut.isPending}>
          {createMut.isPending ? '추가 중…' : '추가'}
        </Button>
      </form>

      {formError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {formError}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '금칙어 목록을 불러오지 못했습니다.'}
        </p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface/50 px-8 py-16 text-center">
          <ShieldBan className="size-8 mx-auto text-foreground-muted" />
          <h3 className="mt-3 text-base font-medium">등록된 금칙어가 없습니다.</h3>
          <p className="mt-1 text-sm text-foreground-muted">위 입력란에서 금칙어를 추가하세요.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">금칙어</th>
                <th className="px-4 py-2.5 text-left font-medium">동작</th>
                <th className="px-4 py-2.5 text-left font-medium">활성</th>
                <th className="px-4 py-2.5 text-left font-medium">메모</th>
                <th className="px-4 py-2.5 text-right font-medium">삭제</th>
              </tr>
            </thead>
            <tbody>
              {items.map((kw: BannedKeyword) => (
                <tr key={kw.id} className="border-t border-border">
                  <td className="px-4 py-3 font-mono">{kw.term}</td>
                  <td className="px-4 py-3">
                    <select
                      value={kw.action}
                      onChange={(e) =>
                        updateMut.mutate({
                          id: kw.id,
                          patch: { action: e.currentTarget.value as BannedKeywordAction },
                        })
                      }
                      className="h-8 rounded-md border border-border bg-surface px-2 text-xs focus:border-accent focus:outline-none"
                    >
                      <option value="block">{ACTION_LABEL.block}</option>
                      <option value="mask">{ACTION_LABEL.mask}</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="size-4 accent-accent"
                        checked={kw.enabled}
                        onChange={(e) =>
                          updateMut.mutate({
                            id: kw.id,
                            patch: { enabled: e.currentTarget.checked },
                          })
                        }
                      />
                    </label>
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground-muted truncate max-w-[280px]">
                    {kw.note ?? ''}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`"${kw.term}" 을 삭제할까요?`)) deleteMut.mutate(kw.id);
                      }}
                      aria-label="삭제"
                      className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-destructive"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
